import { useCallback } from "react";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { ContactPersonSelectOptionDto } from "@advanced-sales-technologies/backend-client/models/ContactPersonSelectOptionDto";
import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";
import { HiOutlineBriefcase } from "react-icons/hi2";

import { useGetContactPersonPositionOptions } from "@/hooks/api/list/options/useGetContactPersonPositionOptions";
import { useGetExternalContactPersonPositionOptions } from "@/hooks/api/list/options/useGetExternalContactPersonPositionOptions";
import BaseColumn from "@/components/molecules/table/BaseColumn";
import HasMoreDataCellPersonRole from "@/components/molecules/table/HasMoreDataCellPersonRole";
import { searchColumns, SearchColumnsProps } from "@/components/organisms/search/searchColumns";

export type ContactPersonColumnProps = {
  onShowPersonColumns?: (show: boolean) => void;
};

type Props = ContactPersonColumnProps & SearchColumnsProps;

const personRoleColumn = ({
  t,
  listId,
  searchParams,
  queryKey,
  onShowPersonColumns,
}: Props): ColumnDef<CompanyEntryDto> =>
  BaseColumn<CompanyEntryDto>({
    accessorFn: (row) => row.point_of_contact?.roles.join("; "),
    id: searchColumns(t).point_of_contact_role.id,
    header: () => searchColumns(t).point_of_contact_role.name,
    cell: ({ row }) => {
      const company = row.original;
      const { id: companyId } = company;
      const contact = company.point_of_contact;

      const buildDisplayValueCb = useCallback(
        (option: ContactPersonSelectOptionDto) => option.roles.map(({ role }) => role).join("; ") || "",
        [],
      );

      const useQueryHookCb = useCallback(
        (query: string | null, enabled: boolean, searchParamsArgs: Record<string, unknown>) =>
          useGetContactPersonPositionOptions(
            {
              companyId: companyId,
              option: {
                query: query,
                list_id: listId,
                configuration: searchParamsArgs,
              },
            },
            enabled,
          ),
        [companyId, listId, useGetContactPersonPositionOptions],
      );

      const useExternalQueryHookCb = useCallback(
        (query: string | null, enabled: boolean, searchParamsArgs: Record<string, unknown>) =>
          useGetExternalContactPersonPositionOptions(
            {
              companyId: companyId,
              option: {
                query: query,
                list_id: listId,
                configuration: searchParamsArgs,
              },
            },
            enabled,
          ),
        [companyId, listId, useGetExternalContactPersonPositionOptions],
      );

      const renderOptionCb = useCallback(
        (option: ContactPersonSelectOptionDto, isSelected: boolean) => (
          <span
            key={option.id}
            className={"flex flex-1 flex-col gap-x-1.5 truncate py-2 text-left text-sm text-quaternary"}
          >
            <div className={clsx("truncate font-medium", isSelected ? "text-primary" : "text-secondary")}>
              {option.roles?.map((role) => `${role.role}${role.city ? ` (${role.city})` : ""}`).join("; ")}
            </div>
            <div className={"truncate"}>{option.name}</div>
          </span>
        ),
        [],
      );

      return (
        <HasMoreDataCellPersonRole<ContactPersonSelectOptionDto>
          queryKey={queryKey}
          company={company}
          buildDisplayValue={buildDisplayValueCb}
          searchParams={searchParams}
          displayValue={contact?.roles.join("; ") || ""}
          initialSelected={contact?.id}
          listId={listId}
          placeholder={searchColumns(t).point_of_contact_role.name}
          onShowPersonColumns={onShowPersonColumns}
          useQueryHook={useQueryHookCb}
          useExternalQueryHook={useExternalQueryHookCb}
          renderOption={renderOptionCb}
        />
      );
    },
    meta: {
      heading: {
        icon: HiOutlineBriefcase,
        className: "!bg-var-secondary-100 hover:!bg-var-secondary-200 active:!bg-var-secondary-300",
      },
      column: {
        className: "bg-var-secondary-50",
      },
    },
  });

export default personRoleColumn;
